import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, Plus, Trash2, Upload, Download, FileText, Image as ImageIcon, X, LayoutGrid, List, Calendar, Pencil, Save, Phone, Globe, CreditCard, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { translateSubType } from '@/utils/transactionTypes';
import { AddTransactionForm } from '@/components/modals/AddTransactionForm';
import { logFileDownload } from '@/components/ActivityTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FILE_TYPES = [
  { key: 'photo', label: 'photo', accept: 'image/*', type: 'image' },
  { key: 'passport', label: 'passport', accept: 'image/*,.pdf', type: 'mixed' },
  { key: 'visa', label: 'visa', accept: 'image/*,.pdf', type: 'mixed' },
  { key: 'passport_cover', label: 'passportCover', accept: 'image/*,.pdf', type: 'mixed' },
];

const DEFAULT_FILE_KEYS = FILE_TYPES.map(ft => ft.key);

// Employee Photo with fallback
const EmployeePhoto = ({ employeeId, employeeName, className = '' }) => {
  const [hasPhoto, setHasPhoto] = useState(true);
  const initials = (employeeName || 'U').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

  if (!hasPhoto) {
    return (
      <div className={`w-24 h-24 rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-2xl font-bold text-slate-500 ${className}`}>
        {initials}
      </div>
    );
  }

  return (
    <img
      src={`${API}/employees/${employeeId}/download/photo`}
      alt={employeeName}
      className={`w-24 h-24 rounded-2xl object-cover bg-slate-100 ${className}`}
      onError={() => setHasPhoto(false)}
    />
  );
};

export const EmployeeDetails = () => {
  const { t, i18n } = useTranslation();
  const { empId } = useParams();
  const navigate = useNavigate();
  const { isAdmin, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [employee, setEmployee] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const fileInputRefs = useRef({});
  const customFileInputRef = useRef(null);
  const isRTL = i18n.language === 'ar';
  const [showCustomUpload, setShowCustomUpload] = useState(false);
  const [customFileName, setCustomFileName] = useState('');
  const [fileViewMode, setFileViewMode] = useState('cards');
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({ name: '', passport_number: '', nationality: '', phone: '' });
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('transactions');

  useEffect(() => { fetchData(); }, [empId]);

  const fetchData = async () => {
    try {
      const [empRes, transRes] = await Promise.all([
        axios.get(`${API}/employees/${empId}`),
        axios.get(`${API}/sub-transactions?employee_id=${empId}`)
      ]);
      setEmployee(empRes.data);
      setTransactions(transRes.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const openEditMode = () => {
    setEditData({
      name: employee?.name || '',
      passport_number: employee?.passport_number || '',
      nationality: employee?.nationality || '',
      phone: employee?.phone || '',
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/employees/${empId}`, editData, { headers });
      setEmployee(prev => ({ ...prev, ...editData }));
      setEditMode(false);
      toast.success(isRTL ? 'تم تحديث البيانات' : 'Data updated');
    } catch { toast.error(isRTL ? 'خطأ في التحديث' : 'Error updating'); }
  };

  const handleFileUpload = async (fileType, event, displayName = '') => {
    const file = event.target.files[0];
    if (!file) return;
    const maxSize = file.type === 'application/pdf' ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) { toast.error(isRTL ? 'حجم الملف كبير' : 'File too large'); return; }
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const payload = { file_type: fileType, file_data: reader.result, file_name: file.name };
        if (displayName) payload.display_name = displayName;
        await axios.post(`${API}/employees/${empId}/upload`, payload, { headers });
        toast.success(isRTL ? 'تم رفع الملف' : 'File uploaded');
        fetchData();
      } catch { toast.error(isRTL ? 'خطأ في الرفع' : 'Upload error'); }
    };
    reader.readAsDataURL(file);
  };

  const handleCustomFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const baseName = customFileName.trim();
    setUploading(true);
    let successCount = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const maxSize = file.type === 'application/pdf' ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) { toast.error(isRTL ? `${file.name} - كبير` : `${file.name} - Too large`); continue; }
      let displayName;
      if (baseName) { displayName = files.length === 1 ? baseName : `${baseName} ${i + 1}`; }
      else { displayName = file.name.replace(/\.[^/.]+$/, ''); }
      const fileKey = `custom_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 6)}`;
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader(); reader.onloadend = () => resolve(reader.result); reader.onerror = reject; reader.readAsDataURL(file);
        });
        await axios.post(`${API}/employees/${empId}/upload`, { file_type: fileKey, file_data: dataUrl, file_name: file.name, display_name: displayName }, { headers });
        successCount++;
      } catch { toast.error(isRTL ? `فشل: ${file.name}` : `Failed: ${file.name}`); }
    }
    setUploading(false);
    if (successCount > 0) { toast.success(isRTL ? `تم رفع ${successCount} ملف` : `${successCount} uploaded`); fetchData(); }
    setCustomFileName(''); setShowCustomUpload(false);
    if (customFileInputRef.current) customFileInputRef.current.value = '';
  };

  const handleDownloadFile = async (fileType, fileName) => {
    try {
      const response = await axios.get(`${API}/employees/${empId}/download/${fileType}`, { responseType: 'blob', headers });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', fileName || `${fileType}.pdf`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success(isRTL ? 'تم التحميل' : 'Downloaded');
      logFileDownload(fileName || fileType, `ملف موظف: ${employee?.name}`);
    } catch { toast.error(isRTL ? 'خطأ' : 'Error'); }
  };

  const hasFile = (fileInfo) => {
    if (!fileInfo) return false;
    if (typeof fileInfo === 'object' && fileInfo.has_data) return true;
    if (typeof fileInfo === 'string' && fileInfo) return true;
    return false;
  };

  const handleDeleteFile = async (fileType) => {
    if (!window.confirm(isRTL ? 'حذف الملف؟' : 'Delete file?')) return;
    try {
      await axios.delete(`${API}/employees/${empId}/files/${fileType}`, { headers });
      toast.success(isRTL ? 'تم الحذف' : 'Deleted');
      fetchData();
    } catch { toast.error(isRTL ? 'خطأ' : 'Error'); }
  };

  const getFileName = (fileInfo, defaultName) => {
    if (!fileInfo) return defaultName;
    if (typeof fileInfo === 'string') return defaultName;
    return fileInfo.name || defaultName;
  };

  const isPdfFile = (fileInfo) => {
    if (!fileInfo) return false;
    const name = typeof fileInfo === 'object' ? (fileInfo.name || '') : '';
    return name.toLowerCase().endsWith('.pdf');
  };

  const handleCancelTransaction = async (transId) => {
    if (window.confirm(isRTL ? 'إلغاء المعاملة؟' : 'Cancel transaction?')) {
      try {
        await axios.put(`${API}/sub-transactions/${transId}/cancel`);
        toast.success(isRTL ? 'تم الإلغاء' : 'Cancelled');
        fetchData();
      } catch { toast.error(t('error')); }
    }
  };

  const handleDeleteTransaction = async (transId) => {
    if (window.confirm(isRTL ? 'حذف نهائي! متأكد؟' : 'Permanent delete! Sure?')) {
      try {
        await axios.delete(`${API}/sub-transactions/${transId}`);
        toast.success(t('success'));
        fetchData();
      } catch { toast.error(t('error')); }
    }
  };

  const getStatusConfig = (status) => {
    const config = {
      completed: { label: isRTL ? 'مكتملة' : 'Completed', bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
      pending: { label: isRTL ? 'قيد الإجراء' : 'In Progress', bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
      waiting_approval: { label: isRTL ? 'بانتظار الموافقة' : 'Awaiting Approval', bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
      cancelled: { label: isRTL ? 'ملغية' : 'Cancelled', bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
      executing: { label: isRTL ? 'جاري التنفيذ' : 'Executing', bg: 'bg-indigo-100', text: 'text-indigo-700', dot: 'bg-indigo-500' },
    };
    return config[status] || config.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: 'var(--theme-accent)' }} />
      </div>
    );
  }

  const sortedTransactions = [...transactions].sort((a, b) => new Date(b.work_date) - new Date(a.work_date));
  const allFileKeys = employee?.files ? Object.keys(employee.files) : [];
  const customFileKeys = allFileKeys.filter(k => !DEFAULT_FILE_KEYS.includes(k));
  const totalUploadedCount = FILE_TYPES.filter(ft => hasFile(employee?.files?.[ft.key])).length + customFileKeys.filter(k => hasFile(employee?.files?.[k])).length;
  const totalFileCount = FILE_TYPES.length + customFileKeys.length;

  return (
    <div className="space-y-5" data-testid="employee-details-page" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* ============ PROFILE HEADER ============ */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {/* Accent bar */}
        <div className="h-1.5" style={{ background: 'linear-gradient(to right, var(--theme-accent, #D4AF37), var(--theme-accent-light, #F3E5AB))' }} />
        
        <div className="p-5">
          <div className={`flex items-start gap-5`}>
            {/* Photo */}
            <EmployeePhoto employeeId={empId} employeeName={employee?.name} />

            {/* Info */}
            <div className={`flex-1 min-w-0`}>
              {employee?.company_name && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full mb-2" 
                  style={{ backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-foreground)', opacity: 0.9 }}
                  data-testid="employee-company-name">
                  {employee.company_name}
                </span>
              )}

              {editMode ? (
                <div className="space-y-3" data-testid="edit-employee-inline">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    <div>
                      <Label className="text-xs text-slate-500">{isRTL ? 'الاسم' : 'Name'}</Label>
                      <Input value={editData.name} onChange={e => setEditData(d => ({ ...d, name: e.target.value }))} className="mt-1 h-9 text-sm" data-testid="edit-inline-name" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">{t('passportNumber')}</Label>
                      <Input value={editData.passport_number} onChange={e => setEditData(d => ({ ...d, passport_number: e.target.value }))} className="mt-1 h-9 text-sm" dir="ltr" data-testid="edit-inline-passport" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">{isRTL ? 'الجنسية' : 'Nationality'}</Label>
                      <Input value={editData.nationality} onChange={e => setEditData(d => ({ ...d, nationality: e.target.value }))} className="mt-1 h-9 text-sm" data-testid="edit-inline-nationality" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">{isRTL ? 'الهاتف' : 'Phone'}</Label>
                      <Input value={editData.phone} onChange={e => setEditData(d => ({ ...d, phone: e.target.value }))} className="mt-1 h-9 text-sm" dir="ltr" data-testid="edit-inline-phone" />
                    </div>
                  </div>
                  <div className={`flex gap-2`}>
                    <Button size="sm" onClick={saveEdit} className="gap-1.5 text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-edit-btn">
                      <Save className="w-3 h-3" /> {isRTL ? 'حفظ' : 'Save'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditMode(false)} className="text-xs h-8" data-testid="cancel-edit-btn">
                      {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                    <h1 className="text-xl md:text-2xl font-bold text-slate-900">{employee?.name}</h1>
                    {isAdmin && (
                      <Button size="sm" variant="ghost" onClick={openEditMode} className="h-7 w-7 p-0 shrink-0 hover:bg-slate-100" data-testid="edit-employee-btn">
                        <Pencil className="w-3.5 h-3.5 text-slate-400" />
                      </Button>
                    )}
                  </div>
                  {/* Info chips */}
                  <div className={`flex items-center gap-3 mt-2.5 flex-wrap`}>
                    {employee?.passport_number && (
                      <div className={`flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 px-2.5 py-1.5 rounded-lg`}>
                        <CreditCard className="w-3.5 h-3.5 text-slate-400" />
                        <span dir="ltr" className="font-mono">{employee.passport_number}</span>
                      </div>
                    )}
                    {employee?.nationality && (
                      <div className={`flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 px-2.5 py-1.5 rounded-lg`}>
                        <Globe className="w-3.5 h-3.5 text-slate-400" />
                        <span>{employee.nationality}</span>
                      </div>
                    )}
                    {employee?.phone && (
                      <div className={`flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 px-2.5 py-1.5 rounded-lg`}>
                        <Phone className="w-3.5 h-3.5 text-slate-400" />
                        <span dir="ltr" className="font-mono">{employee.phone}</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Back button */}
            <Button onClick={() => {
              const companyId = employee?.company_id || employee?.establishment_id;
              if (companyId) { navigate(`/employees?company=${companyId}`); } else { navigate('/employees'); }
            }} variant="outline" size="icon" className="h-9 w-9 shrink-0 rounded-xl" data-testid="back-btn">
              {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
            </Button>
          </div>

          {/* Stats row */}
          <div className={`flex items-center gap-4 mt-4 pt-4 border-t border-slate-100`}>
            <div className={`flex items-center gap-2`}>
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
              <div className="">
                <p className="text-lg font-bold text-slate-800">{sortedTransactions.length}</p>
                <p className="text-[10px] text-slate-400">{isRTL ? 'معاملة' : 'Transactions'}</p>
              </div>
            </div>
            <div className="w-px h-8 bg-slate-200" />
            <div className={`flex items-center gap-2`}>
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Download className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="">
                <p className="text-lg font-bold text-slate-800">{totalUploadedCount}</p>
                <p className="text-[10px] text-slate-400">{isRTL ? 'ملف' : 'Files'}</p>
              </div>
            </div>
            <div className="w-px h-8 bg-slate-200" />
            <div className={`flex items-center gap-2`}>
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                <CreditCard className="w-4 h-4 text-amber-600" />
              </div>
              <div className="">
                <p className="text-lg font-bold text-slate-800" dir="ltr">
                  {sortedTransactions.reduce((s, t) => s + (t.price || 0), 0).toLocaleString()}
                </p>
                <p className="text-[10px] text-slate-400">AED</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ TABS ============ */}
      <div className={`flex gap-2`}>
        <button
          onClick={() => setActiveTab('transactions')}
          data-testid="tab-transactions"
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
            activeTab === 'transactions'
              ? 'text-white shadow-sm'
              : 'bg-white border border-slate-200 text-slate-500 hover:text-slate-700'
          }`}
          style={activeTab === 'transactions' ? { backgroundColor: 'var(--theme-primary, #0F172A)' } : {}}
        >
          {t('employeeTransactions')} ({sortedTransactions.length})
        </button>
        <button
          onClick={() => setActiveTab('files')}
          data-testid="tab-files"
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
            activeTab === 'files'
              ? 'text-white shadow-sm'
              : 'bg-white border border-slate-200 text-slate-500 hover:text-slate-700'
          }`}
          style={activeTab === 'files' ? { backgroundColor: 'var(--theme-primary, #0F172A)' } : {}}
        >
          {t('employeeFiles')} ({totalUploadedCount}/{totalFileCount})
        </button>
      </div>

      {/* ============ TRANSACTIONS TAB ============ */}
      {activeTab === 'transactions' && (
        <div className="space-y-3">
          {/* Add button */}
          {isAdmin && (
            <div className="">
              <Button size="sm" onClick={() => setShowAddTransaction(!showAddTransaction)}
                className="h-9 px-4 text-sm gap-2"
                style={{ backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-foreground)' }}>
                <Plus className="w-4 h-4" />
                {t('addTransaction')}
              </Button>
            </div>
          )}

          {(showAddTransaction || editingTransaction) && (
            <AddTransactionForm
              employeeId={empId}
              isRTL={isRTL}
              editData={editingTransaction}
              onClose={() => { setShowAddTransaction(false); setEditingTransaction(null); }}
              onSuccess={fetchData}
            />
          )}

          {/* Transaction Cards */}
          <div className="space-y-2">
            {sortedTransactions.map((trans) => {
              const isCancelled = trans.status === 'cancelled';
              const statusConf = getStatusConfig(trans.status);

              return (
                <div
                  key={trans.id}
                  data-testid={`transaction-card-${trans.id}`}
                  className={`rounded-xl border bg-white overflow-hidden transition-all hover:shadow-sm ${
                    isCancelled ? 'opacity-60 border-red-200' : 'border-slate-200'
                  }`}
                >
                  <div className={`flex items-center gap-4 p-4`}>
                    {/* Status dot */}
                    <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${statusConf.dot}`} />
                    
                    {/* Main info */}
                    <div className={`flex-1 min-w-0`}>
                      <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                        <span className={`font-semibold text-sm text-slate-800 ${isCancelled ? 'line-through' : ''}`}>
                          {translateSubType(trans.sub_type, isRTL) || t(trans.main_type)}
                        </span>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${statusConf.bg} ${statusConf.text}`}>
                          {statusConf.label}
                        </span>
                      </div>
                      <div className={`flex items-center gap-3 mt-1 text-xs text-slate-400`}>
                        <span>{t(trans.main_type)}</span>
                        <span dir="ltr">{new Date(trans.work_date).toLocaleDateString('en-GB')}</span>
                      </div>
                    </div>

                    {/* Price */}
                    <div className={`text-right shrink-0 ${isRTL ? 'text-left' : ''}`}>
                      <span className={`text-base font-bold ${isCancelled ? 'line-through text-red-400' : 'text-slate-800'}`} dir="ltr">
                        {trans.price?.toLocaleString()} AED
                      </span>
                      {isAdmin && trans.actual_paid_amount > 0 && trans.actual_paid_amount !== trans.price && (
                        <p className="text-[10px] text-slate-400 mt-0.5" dir="ltr">
                          {isRTL ? 'المدفوع:' : 'Paid:'} {trans.actual_paid_amount?.toLocaleString()} AED
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    {isAdmin && (
                      <div className={`flex items-center gap-0.5 shrink-0`}>
                        <Button size="sm" variant="ghost" data-testid={`edit-trans-${trans.id}`}
                          onClick={() => { setEditingTransaction(trans); setShowAddTransaction(false); }}
                          className="h-8 w-8 p-0 hover:bg-blue-50">
                          <Pencil className="w-3.5 h-3.5 text-blue-500" />
                        </Button>
                        {!isCancelled && (
                          <Button size="sm" variant="ghost" onClick={() => handleCancelTransaction(trans.id)}
                            className="h-8 w-8 p-0 hover:bg-orange-50" data-testid={`cancel-trans-${trans.id}`}>
                            <X className="w-3.5 h-3.5 text-orange-500" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteTransaction(trans.id)}
                          className="h-8 w-8 p-0 hover:bg-red-50" data-testid={`delete-trans-${trans.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {sortedTransactions.length === 0 && (
            <div className="text-center py-16">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <FileText className="w-7 h-7 text-slate-300" />
              </div>
              <p className="text-slate-400 text-sm">{t('noData')}</p>
            </div>
          )}
        </div>
      )}

      {/* ============ FILES TAB ============ */}
      {activeTab === 'files' && (
        <div className="space-y-3">
          {/* View toggle */}
          <div className={`flex items-center justify-between`}>
            <span className="text-sm font-medium text-slate-500">
              {totalUploadedCount} / {totalFileCount} {isRTL ? 'ملفات مرفوعة' : 'files uploaded'}
            </span>
            <div className="flex bg-slate-100 rounded-lg p-0.5">
              <button data-testid="view-mode-cards" onClick={() => setFileViewMode('cards')}
                className={`p-1.5 rounded-md transition-all ${fileViewMode === 'cards' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-400'}`}>
                <LayoutGrid className="w-3.5 h-3.5" />
              </button>
              <button data-testid="view-mode-compact" onClick={() => setFileViewMode('compact')}
                className={`p-1.5 rounded-md transition-all ${fileViewMode === 'compact' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-400'}`}>
                <List className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Card view */}
          {fileViewMode === 'cards' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              {FILE_TYPES.map((fileType) => {
                const fileInfo = employee?.files?.[fileType.key];
                const fileExists = hasFile(fileInfo);
                const fileName = getFileName(fileInfo, `${fileType.key}.pdf`);
                const isPdf = isPdfFile(fileInfo);
                const isImage = fileExists && !isPdf;
                const imageUrl = isImage ? `${API}/employees/${empId}/download/${fileType.key}` : null;

                return (
                  <div key={fileType.key} data-testid={`file-row-${fileType.key}`} dir={isRTL ? 'rtl' : 'ltr'}
                    className={`rounded-xl border overflow-hidden transition-all ${fileExists ? 'border-slate-200 bg-white hover:shadow-md' : 'border-dashed border-slate-200 bg-slate-50/50'}`}>
                    <div className="relative h-36 bg-slate-50 flex items-center justify-center overflow-hidden">
                      {isImage && imageUrl ? (
                        <img src={imageUrl} alt={t(fileType.label)}
                          className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                          onClick={() => window.open(imageUrl, '_blank')} />
                      ) : isPdf && fileExists ? (
                        <div className="flex flex-col items-center gap-2 p-3">
                          <div className="w-14 h-14 rounded-xl bg-red-100 flex items-center justify-center">
                            <FileText className="w-7 h-7 text-red-500" />
                          </div>
                          <span className="text-[10px] text-slate-400 text-center truncate max-w-full px-2" dir="ltr">{fileName}</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                            <Upload className="w-5 h-5 text-slate-300" />
                          </div>
                          <span className="text-[10px] text-slate-300">{isRTL ? 'لم يتم الرفع' : 'Not uploaded'}</span>
                        </div>
                      )}
                      {fileExists && (
                        <div className={`absolute top-2 ${isRTL ? 'left-2' : 'right-2'}`}>
                          <span className="text-[9px] font-semibold text-white bg-emerald-500 px-2 py-0.5 rounded-full shadow-sm">
                            {isPdf ? 'PDF' : 'IMG'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-2.5">
                      <p className="text-xs font-semibold text-slate-700 truncate mb-2">{t(fileType.label)}</p>
                      <div className="flex items-center gap-1.5">
                        {fileExists ? (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleDownloadFile(fileType.key, fileName)}
                              data-testid={`download-${fileType.key}-btn`} className="h-7 px-2 text-[10px] gap-1 flex-1">
                              <Download className="w-3 h-3" />{isRTL ? 'تحميل' : 'Download'}
                            </Button>
                            {isAdmin && (
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteFile(fileType.key)}
                                data-testid={`delete-file-${fileType.key}-btn`} className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50">
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </>
                        ) : (
                          isAdmin && (
                            <Button size="sm" variant="outline" onClick={() => fileInputRefs.current[fileType.key]?.click()}
                              data-testid={`upload-trigger-${fileType.key}-btn`} className="h-7 px-2 text-[10px] gap-1 flex-1 border-dashed"
                              style={{ borderColor: 'var(--theme-accent)', color: 'var(--theme-accent)' }}>
                              <Upload className="w-3 h-3" />{isRTL ? 'رفع' : 'Upload'}
                            </Button>
                          )
                        )}
                      </div>
                    </div>
                    <input ref={(el) => fileInputRefs.current[fileType.key] = el} type="file"
                      accept={fileType.accept} onChange={(e) => handleFileUpload(fileType.key, e)} className="hidden" />
                  </div>
                );
              })}

              {/* Custom Files */}
              {customFileKeys.map((fileKey) => {
                const fileInfo = employee?.files?.[fileKey];
                const fileExists = hasFile(fileInfo);
                const fileName = getFileName(fileInfo, `${fileKey}.pdf`);
                const displayName = fileInfo?.display_name || fileKey;
                const isPdf = isPdfFile(fileInfo);
                const isImage = fileExists && !isPdf;
                const imageUrl = isImage ? `${API}/employees/${empId}/download/${fileKey}` : null;

                return (
                  <div key={fileKey} data-testid={`file-row-${fileKey}`} dir={isRTL ? 'rtl' : 'ltr'}
                    className="rounded-xl border overflow-hidden border-slate-200 bg-white hover:shadow-md transition-all">
                    <div className="relative h-36 bg-slate-50 flex items-center justify-center overflow-hidden">
                      {isImage && imageUrl ? (
                        <img src={imageUrl} alt={displayName}
                          className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                          onClick={() => window.open(imageUrl, '_blank')} />
                      ) : (
                        <div className="flex flex-col items-center gap-2 p-3">
                          <div className="w-14 h-14 rounded-xl bg-red-100 flex items-center justify-center">
                            <FileText className="w-7 h-7 text-red-500" />
                          </div>
                          <span className="text-[10px] text-slate-400 text-center truncate max-w-full px-2" dir="ltr">{fileName}</span>
                        </div>
                      )}
                      <div className={`absolute top-2 ${isRTL ? 'left-2' : 'right-2'}`}>
                        <span className="text-[9px] font-semibold text-white bg-emerald-500 px-2 py-0.5 rounded-full shadow-sm">
                          {isPdf ? 'PDF' : 'IMG'}
                        </span>
                      </div>
                    </div>
                    <div className="p-2.5">
                      <p className="text-xs font-semibold text-slate-700 truncate mb-2">{displayName}</p>
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => handleDownloadFile(fileKey, fileName)}
                          data-testid={`download-${fileKey}-btn`} className="h-7 px-2 text-[10px] gap-1 flex-1">
                          <Download className="w-3 h-3" />{isRTL ? 'تحميل' : 'Download'}
                        </Button>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteFile(fileKey)}
                            data-testid={`delete-file-${fileKey}-btn`} className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Compact view */}
          {fileViewMode === 'compact' && (
            <div className="space-y-2">
              {FILE_TYPES.map((fileType) => {
                const fileInfo = employee?.files?.[fileType.key];
                const fileExists = hasFile(fileInfo);
                const fileName = getFileName(fileInfo, `${fileType.key}.pdf`);
                const isPdf = isPdfFile(fileInfo);
                const uploadedAt = fileInfo?.uploaded_at;

                return (
                  <div key={fileType.key} data-testid={`file-compact-${fileType.key}`} dir={isRTL ? 'rtl' : 'ltr'}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                      fileExists ? 'border-slate-200 bg-white hover:bg-slate-50/80' : 'border-dashed border-slate-200 bg-slate-50/30'
                    }`}>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                      fileExists ? (isPdf ? 'bg-red-100' : 'bg-blue-100') : 'bg-slate-100'
                    }`}>
                      {fileExists ? (isPdf ? <FileText className="w-5 h-5 text-red-500" /> : <ImageIcon className="w-5 h-5 text-blue-500" />) : <Upload className="w-4 h-4 text-slate-300" />}
                    </div>
                    <div className={`flex-1 min-w-0`}>
                      <p className="text-sm font-medium text-slate-700">{t(fileType.label)}</p>
                      {fileExists ? (
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-slate-400 truncate" dir="ltr">{fileName}</span>
                          {uploadedAt && (
                            <span className="text-[10px] text-slate-400" dir="ltr">{new Date(uploadedAt).toLocaleDateString('en-GB')}</span>
                          )}
                        </div>
                      ) : (
                        <p className="text-[10px] text-slate-300 mt-0.5">{isRTL ? 'لم يتم الرفع' : 'Not uploaded'}</p>
                      )}
                    </div>
                    {fileExists && (
                      <span className={`text-[10px] font-semibold px-2 py-1 rounded-md shrink-0 ${isPdf ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
                        {isPdf ? 'PDF' : 'IMG'}
                      </span>
                    )}
                    <div className="flex items-center gap-1 shrink-0">
                      {fileExists ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => handleDownloadFile(fileType.key, fileName)}
                            data-testid={`download-compact-${fileType.key}-btn`} className="h-8 px-2.5 text-[11px] gap-1">
                            <Download className="w-3.5 h-3.5" />{isRTL ? 'تحميل' : 'Download'}
                          </Button>
                          {isAdmin && (
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteFile(fileType.key)}
                              data-testid={`delete-compact-${fileType.key}-btn`} className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </>
                      ) : (
                        isAdmin && (
                          <Button size="sm" variant="outline" onClick={() => fileInputRefs.current[fileType.key]?.click()}
                            data-testid={`upload-compact-${fileType.key}-btn`} className="h-8 px-2.5 text-[11px] gap-1 border-dashed"
                            style={{ borderColor: 'var(--theme-accent)', color: 'var(--theme-accent)' }}>
                            <Upload className="w-3.5 h-3.5" />{isRTL ? 'رفع' : 'Upload'}
                          </Button>
                        )
                      )}
                    </div>
                    <input ref={(el) => fileInputRefs.current[fileType.key] = el} type="file"
                      accept={fileType.accept} onChange={(e) => handleFileUpload(fileType.key, e)} className="hidden" />
                  </div>
                );
              })}

              {/* Custom files compact */}
              {customFileKeys.map((fileKey) => {
                const fileInfo = employee?.files?.[fileKey];
                const fileName = getFileName(fileInfo, `${fileKey}.pdf`);
                const displayName = fileInfo?.display_name || fileKey;
                const isPdf = isPdfFile(fileInfo);
                const uploadedAt = fileInfo?.uploaded_at;

                return (
                  <div key={fileKey} data-testid={`file-compact-${fileKey}`} dir={isRTL ? 'rtl' : 'ltr'}
                    className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50/80 transition-colors">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isPdf ? 'bg-red-100' : 'bg-blue-100'}`}>
                      {isPdf ? <FileText className="w-5 h-5 text-red-500" /> : <ImageIcon className="w-5 h-5 text-blue-500" />}
                    </div>
                    <div className={`flex-1 min-w-0`}>
                      <p className="text-sm font-medium text-slate-700">{displayName}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-slate-400 truncate" dir="ltr">{fileName}</span>
                        {uploadedAt && <span className="text-[10px] text-slate-400" dir="ltr">{new Date(uploadedAt).toLocaleDateString('en-GB')}</span>}
                      </div>
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-1 rounded-md shrink-0 ${isPdf ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
                      {isPdf ? 'PDF' : 'IMG'}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => handleDownloadFile(fileKey, fileName)}
                        data-testid={`download-compact-${fileKey}-btn`} className="h-8 px-2.5 text-[11px] gap-1">
                        <Download className="w-3.5 h-3.5" />{isRTL ? 'تحميل' : 'Download'}
                      </Button>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteFile(fileKey)}
                          data-testid={`delete-compact-${fileKey}-btn`} className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add custom file */}
          {isAdmin && (
            <div className="mt-3">
              {showCustomUpload ? (
                <div className={`p-4 border border-dashed border-slate-300 rounded-xl bg-white space-y-3`} dir={isRTL ? 'rtl' : 'ltr'}>
                  <div className={`flex items-center justify-between`}>
                    <p className="text-sm font-medium text-slate-700">{isRTL ? 'إضافة ملف جديد' : 'Add New File'}</p>
                    <Button size="sm" variant="ghost" onClick={() => { setShowCustomUpload(false); setCustomFileName(''); }} className="h-7 w-7 p-0">
                      <X className="w-4 h-4 text-slate-400" />
                    </Button>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">{isRTL ? 'اسم الملفات (اختياري)' : 'Name (optional)'}</Label>
                    <Input data-testid="custom-file-name-input" value={customFileName}
                      onChange={(e) => setCustomFileName(e.target.value)}
                      placeholder={isRTL ? 'مثال: عقد العمل' : 'e.g. Work Contract'}
                      className="h-9 text-sm" dir={isRTL ? 'rtl' : 'ltr'} />
                  </div>
                  <Button size="sm" variant="outline" disabled={uploading} data-testid="custom-file-upload-trigger-btn"
                    onClick={() => customFileInputRef.current?.click()}
                    className="h-9 text-xs gap-1.5 w-full border-dashed"
                    style={{ borderColor: 'var(--theme-accent)', color: 'var(--theme-accent)' }}>
                    {uploading ? (
                      <><div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" /> {isRTL ? 'جاري الرفع...' : 'Uploading...'}</>
                    ) : (
                      <><Upload className="w-3.5 h-3.5" /> {isRTL ? 'اختر ملف أو أكثر' : 'Choose File(s)'}</>
                    )}
                  </Button>
                  <input ref={customFileInputRef} type="file" accept="image/*,.pdf" multiple
                    onChange={handleCustomFileUpload} className="hidden" data-testid="custom-file-upload-input" />
                </div>
              ) : (
                <Button data-testid="add-custom-file-btn" variant="outline" onClick={() => setShowCustomUpload(true)}
                  className="w-full h-10 text-sm gap-2 border-dashed border-slate-300 text-slate-500 hover:text-slate-700 hover:border-slate-400 rounded-xl">
                  <Plus className="w-4 h-4" /> {isRTL ? 'إضافة ملف جديد' : 'Add New File'}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
